/**
 * EditorialTimeline — two editorial timeline treatments behind one API:
 *
 *   variant='gantt'  — a mini-gantt: named rows, each a hairline track with
 *                      a positioned accent bar (offset + width as 0..1
 *                      fractions of the axis), plus low/mid/high axis ticks.
 *                      Reference: `.tl` in round3-water-climate-env.html
 *                      (Env 11B permitting gantt).
 *   variant='phased' — recommendations grouped under their phase, each phase
 *                      a mono label + a numbered action list. Reference:
 *                      `.rtl` in round3-recs-feasibility-finance.html
 *                      (Recs 17B phased timeline).
 *
 * Used by:
 *   - Environmental (11B)            — §404 pre-app / delineation / review (gantt)
 *   - Action Plan & Recommendations  — phased action sequencing (phased)
 *
 * Theme tokens read: 'accent' (bars, phase labels, action numbers), 'hair'
 * (track + phase rules), 'paper-2'-equivalent 'subtleBg' (empty track),
 * 'negative' (a `tone:'risk'` gantt bar), 'ink' (action text), 'muted'
 * (phase window + axis ticks), 'faint' (axis). Mono labels via
 * --report-body-font. Pure / print-safe.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { reportShellBodyFontSize, REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export interface GanttRow {
  label: React.ReactNode;
  /** Bar start as a 0..1 fraction of the axis. */
  start: number;
  /** Bar width as a 0..1 fraction of the axis. */
  width: number;
  /** 'risk' tints the bar with the negative role (a critical-path window). */
  tone?: 'accent' | 'risk';
}

export interface PhaseGroup {
  label: React.ReactNode;
  /** Optional right-aligned window note, e.g. "LOI → +12mo". */
  window?: React.ReactNode;
  /** Action lines under this phase. */
  actions: React.ReactNode[];
}

export interface EditorialTimelineProps {
  theme: ReportTheme;
  variant: 'gantt' | 'phased';
  /** For variant='gantt'. */
  rows?: GanttRow[];
  /** For variant='gantt' — axis tick labels left→right (e.g. LOI / +12mo / +24mo). */
  axisTicks?: React.ReactNode[];
  /** For variant='phased' — counter base so action numbers run continuously. */
  phases?: PhaseGroup[];
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

function GanttTimeline({
  theme,
  rows,
  axisTicks,
}: {
  theme: ReportTheme;
  rows: GanttRow[];
  axisTicks?: React.ReactNode[];
}): React.ReactElement | null {
  const usable = rows.filter((r) => Number.isFinite(r.start) && Number.isFinite(r.width));
  if (usable.length === 0) return null;

  const accent = roleColor(theme, 'accent');
  const risk = roleColor(theme, 'negative');
  const hair = roleColor(theme, 'hair');
  const track = roleColor(theme, 'subtleBg');
  const ink = roleColor(theme, 'ink');
  const faint = roleColor(theme, 'faint');
  const labelCol = 150;

  return (
    <div>
      {usable.map((row, i) => (
        <div
          key={i}
          style={{
            // PAGEDJS-ROBUST — FLEX, not grid (see the phased rows below): pagedjs
            // collapses/drops inline `display:grid` rows at a page break. `labelCol px
            // 1fr` grid → `flex:0 0 labelCol` label + `flex:1 1 auto` track is identical.
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            padding: '5px 0',
          }}
        >
          <div
            style={{
              fontFamily: MONO,
              fontSize: REPORT_TEXT_PX.caption,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: ink,
              flex: `0 0 ${labelCol}px`,
            }}
          >
            {row.label}
          </div>
          <div style={{ position: 'relative', height: 12, background: track, border: `1px solid ${hair}`, flex: '1 1 auto', minWidth: 0 }}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${Math.max(0, Math.min(1, row.start)) * 100}%`,
                width: `${Math.max(0, Math.min(1, row.width)) * 100}%`,
                background: row.tone === 'risk' ? risk : accent,
              }}
            />
          </div>
        </div>
      ))}
      {axisTicks && axisTicks.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: MONO,
            fontSize: 8.5,
            color: faint,
            marginTop: 5,
            paddingLeft: labelCol + 12,
          }}
        >
          {axisTicks.map((t, i) => (
            <span key={i}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/** An action node is renderable when it is not null/empty (whitespace-only
 *  strings count as empty so a blank phase collapses). */
function isRenderableAction(node: React.ReactNode): boolean {
  if (node == null || node === false) return false;
  if (typeof node === 'string') return node.trim().length > 0;
  if (Array.isArray(node)) return node.some(isRenderableAction);
  return true;
}

function PhasedTimeline({
  theme,
  phases,
}: {
  theme: ReportTheme;
  phases: PhaseGroup[];
}): React.ReactElement | null {
  const usable = phases
    .map((p) => ({ ...p, actions: p.actions.filter(isRenderableAction) }))
    .filter((p) => p.actions.length > 0);
  if (usable.length === 0) return null;

  const accent = roleColor(theme, 'accent');
  const hair = roleColor(theme, 'hair');
  const muted = roleColor(theme, 'muted');
  const ink = roleColor(theme, 'ink');

  let counter = 0;
  return (
    <div>
      {usable.map((phase, p) => (
        <div key={p} style={{ marginBottom: 14 }}>
          <div
            style={{
              fontFamily: MONO,
              fontSize: REPORT_TEXT_PX.caption,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: accent,
              borderBottom: `1px solid ${hair}`,
              paddingBottom: 4,
              marginBottom: 7,
              display: 'flex',
              justifyContent: 'space-between',
              breakAfter: 'avoid',
              pageBreakAfter: 'avoid',
            }}
          >
            <span>{phase.label}</span>
            {phase.window != null && <span style={{ color: muted }}>{phase.window}</span>}
          </div>
          {phase.actions
            .map((action, a) => {
              counter += 1;
              const n = counter;
              return (
                <div
                  key={a}
                  style={{
                    // PAGEDJS-ROBUST — FLEX, not grid. pagedjs 0.4.3 COLLAPSES inline
                    // `display:grid` rows during fragmentation/clone (every row painted
                    // at the same y, or the whole section body dropped when it must
                    // cross a page boundary — the actionPlan blank-tail defect). Flexbox
                    // survives the clone, matching the EditorialRecommendations rows.
                    // `18px 1fr` grid → `flex:0 0 18px` numeral + `flex:1 1 auto` text is
                    // visually identical.
                    display: 'flex',
                    gap: 9,
                    fontSize: reportShellBodyFontSize(theme),
                    color: ink,
                    padding: '3px 0',
                  }}
                >
                  <span style={{ fontFamily: MONO, fontSize: REPORT_TEXT_PX.caption, color: accent, flex: '0 0 18px' }}>
                    {String(n).padStart(2, '0')}
                  </span>
                  <span style={{ flex: '1 1 auto', minWidth: 0 }}>{action}</span>
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
}

export function EditorialTimeline({
  theme,
  variant,
  rows,
  axisTicks,
  phases,
  id,
  className,
  style,
}: EditorialTimelineProps): React.ReactElement | null {
  // Detect emptiness from the SOURCE data (not the child element, which is
  // always a truthy React element even when it renders null) so the outer
  // wrapper is omitted entirely for an empty timeline.
  if (variant === 'gantt') {
    const usable = (rows ?? []).filter((r) => Number.isFinite(r.start) && Number.isFinite(r.width));
    if (usable.length === 0) return null;
    return (
      <div id={id} className={className} style={style}>
        <GanttTimeline theme={theme} rows={rows ?? []} axisTicks={axisTicks} />
      </div>
    );
  }

  const usablePhases = (phases ?? [])
    .map((p) => p.actions.filter(isRenderableAction))
    .filter((a) => a.length > 0);
  if (usablePhases.length === 0) return null;
  return (
    <div id={id} className={className} style={style}>
      <PhasedTimeline theme={theme} phases={phases ?? []} />
    </div>
  );
}
